import { useCallback, useMemo } from "react";

import fetchMoreUpdateQueryPreviousFix from "apollo/fetchMoreUpdateQueryPreviousFix";
import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import { uniqByKey } from "utils/uniq";

import { ChangeHistoryDateGroup } from "./types";
import { CHANGE_HISTORY_QUERY, ChangeHistoryQueryData } from "./gql";
import computeChangeHistoryGroups from "./helpers";

type UseTemplateDeploymentChangeHistoryParams = {
  templateId: string;
  deploymentId: string;
};

const ITEMS_PER_PAGE = 50;

const DEFAULT_SEARCH_INPUT = {
  first: ITEMS_PER_PAGE,
  after: null,
  fullTextSearch: null,
  orderBy: null,
  predicates: null,
  requestedPage: null,
};

const useTemplateDeploymentChangeHistory = (
  params: UseTemplateDeploymentChangeHistoryParams
): {
  dateGroups: ChangeHistoryDateGroup[];
  loading: boolean;
  initialLoading: boolean;
  hasNextPage: boolean;
  loadMore: () => Promise<void>;
  error: Error | undefined;
} => {
  const { data, loading, error, fetchMore } = usePolledQuery(CHANGE_HISTORY_QUERY, {
    pollingGroup: PollingIntervalGroups.Lists,
    variables: {
      templateID: params.templateId,
      id: params.deploymentId,
      input: DEFAULT_SEARCH_INPUT,
    },
  });

  const pageInfo = data?.templateDeployment?.searchChangeHistory?.pageInfo;
  const records = useMemo(
    () => data?.templateDeployment?.searchChangeHistory?.edges.map((e) => e.node) ?? [],
    [data]
  );
  const dateGroups = useMemo(() => computeChangeHistoryGroups(records), [records]);

  const loadMore = useCallback(async () => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;

    await fetchMore({
      variables: { input: { ...DEFAULT_SEARCH_INPUT, after: pageInfo.endCursor } },
      updateQuery: (prev, { fetchMoreResult }) => {
        const previousData = fetchMoreUpdateQueryPreviousFix(
          !!prev?.templateDeployment,
          prev,
          data
        ) as ChangeHistoryQueryData;

        if (!fetchMoreResult?.templateDeployment?.searchChangeHistory) return previousData;

        const existingEdges = previousData?.templateDeployment?.searchChangeHistory?.edges ?? [];
        const newEdges = fetchMoreResult.templateDeployment.searchChangeHistory.edges;

        return {
          templateDeployment: {
            ...fetchMoreResult.templateDeployment,
            searchChangeHistory: {
              ...fetchMoreResult.templateDeployment.searchChangeHistory,
              edges: uniqByKey([...existingEdges, ...newEdges], "cursor"),
            },
          },
        };
      },
    });
  }, [fetchMore, pageInfo, data]);

  return {
    dateGroups,
    loading,
    initialLoading: loading && !data,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    loadMore,
    error,
  };
};

export default useTemplateDeploymentChangeHistory;
